/**
 * Raiz do app (§2.2 bloco App; FE §5.2): `RootErrorBoundary` › `#app-root` (AppShell) + portal `#dialog-root`
 * (AboutDialog, chunk sob demanda; DE-24) + `LiveRegion` (única região viva, fora de `#app-root`, §15.3).
 * Na montagem (uma vez, antes do primeiro quadro): modalidade de teclado, cascata do Esc e atalhos com as ações
 * injetadas (DE-B17), foco do roteador por `focusIfKeyboard` (§15.2.1), movimento reduzido reativo (M29) e as marcas
 * `data-hidden`/`low-power` do `<html>` (FE §11.4). Depois do primeiro quadro com o AppShell, `window.__bootOk()` uma
 * única vez (§12.7). O resize/orientation é do useLayoutMode (AppShell) e o `hashchange` é do roteador (§5.3.1).
 * Fase 3 (§17.2 item 7): com a ligação viva (`runtime`, criada pelo main.tsx), a montagem liga os efeitos globais
 * (`startEffects`: tique de `now` pelo relógio da fonte, relógio dos dados; DE-B03) e confirma o `FeedClient` nos sinais
 * (`FeedRuntime.start`, já chamado pelo main.tsx antes do 1º render, DECISOES §40: `feed`, `liveView`, `historySeries`, aba oculta, rede, bfcache); a `LiveRegion` recebe o relógio
 * da fonte e os modelos de §13.5 (forma `sr`); o `DevPanel` (§14.9.9) vem por `loadDevPanel()` só com
 * `__SIM_ENABLED__` e `painel=1` (DE-B13). Sem `runtime` (testes de unidade), a casca monta sem feed.
 * Fase 4 (§7.1): o herói nacional (`PresidentHero`, app/heroSlot.tsx) nas regiões `left` e `card`; o DevPanel recebe
 * os sinais `phase` e `headline` (§13.1.2, §13.2).
 * Fases 6 e 7a (§7.1, §4.4.2, §4.6.2): painéis (app/panelsSlot.tsx) na coluna esquerda (gráfico ou `ProbabilitySlot`
 * depois do herói), na direita sem foco e na seção "Mais" do `flow`; cartão do foco (app/placesSlot.tsx) na direita
 * com foco e na região `card`.
 * Fases 8a e 9 (§10.2, §11.1): a linha do tempo (app/timelineSlot.tsx) na região `timeline` de todos os modos menos a
 * TV; na aba Governadores, o `GovernorsPanel` (app/governorsSlot.tsx, no chunk `index`) no lugar do herói e do gráfico.
 */
import type { ComponentType } from 'preact';
import { useEffect, useLayoutEffect, useState } from 'preact/hooks';
import { installEscape } from '../a11y/escape';
import { focusIfKeyboard } from '../a11y/focus';
import { LiveRegion } from '../a11y/LiveRegion';
import { installShortcuts } from '../a11y/shortcuts';
import { installKeyboardModality } from '../a11y/useKeyboardModality';
import { installReducedMotion } from '../a11y/useReducedMotion';
import type { ElectionEvent } from '../contracts/events';
import { eventText } from '../copy/events';
import { SignOutButton } from '../features/door/SignOutButton';
import { ErrorBoundary } from '../features/errors/ErrorBoundary';
import { RootErrorBoundary } from '../features/errors/RootErrorBoundary';
import { NoticeBar } from '../features/header/NoticeBar';
import { OfficeTabs } from '../features/header/OfficeTabs';
import { SimulationBanner } from '../features/header/SimulationBanner';
import { SourceStamp } from '../features/header/SourceStamp';
import { TopBar } from '../features/header/TopBar';
import { mapZoom } from '../features/map/mapLink';
import { SearchPalette } from '../features/search/SearchPalette';
import type { RouteOffice } from '../model/types';
import { configureRouterEffects } from '../router/effects';
import {
  closeAbout,
  closeProfile,
  currentRoute,
  exitTv,
  getOneKeyShortcuts,
  setOffice,
  setScreen,
  togglePicker,
  toggleSearch,
  upOne,
} from '../state/actions';
import { headline, offices, phase } from '../state/derived';
import { startEffects } from '../state/effects';
import { useLayout, useTimeline, useTweenDuration, useUi, useView } from '../state/hooks';
import { liveView, statics } from '../state/store';
import { AppShell, type ShellBoundaryProps, type ShellSlots } from './AppShell';
import { CardCommentarySlot, CommentaryProbeSlot, NarrowCommentarySlot } from './commentarySlot';
import { type DevPanelHostProps, devPanelWanted, loadDevPanel } from './createSource';
import type { FeedRuntime } from './feedBinding';
import { GovernorsSlot } from './governorsSlot';
import { PresidentHero } from './heroSlot';
import { installHiddenEffects } from './hiddenEffects';
import { isPhoneMode } from './layoutMode';
import { MapSlot, MapTopSlot, useLiveMunicipalLayer, ZoomSlot } from './mapSlot';
import { MathCardSlot } from './mathCardSlot';
import { MorePanels, PanelsIntro, RightPanels, TrendSlot } from './panelsSlot';
import { PartnerBarSlot } from './partnerSlot';
import { PhaseScreen } from './phaseScreen';
import { PlaceSlot } from './placesSlot';
import { useScreenReturn } from './screenNav';
import { LiveScreenSlot, LiveScreenToggle, useLiveScreenOn } from './screenSlot';
import { publicClock, type SiteAccess, sessionEmail, signOut } from './siteGate';
import { appOpen, installAfterKeepsApp, installPhaseTitle, phaseTitle, sitePhase } from './sitePhase';
import { chunkFailed } from './staleBuild';
import { TimelineSlot } from './timelineSlot';

interface BootWindow {
  __bootOk?: () => void;
}

/**
 * Aperto de mão com a BootGuard (§12.7, AC-2.2-03): o App monta uma vez por documento (a RootErrorBoundary fica dentro
 * dele, e um reset não o remonta), então o efeito de montagem chama `__bootOk()` uma única vez.
 */
function signalBootOk(): void {
  (window as BootWindow).__bootOk?.();
}

/** Cabeçalho mínimo da boundary `header` (§12.8): só o nome do produto (a SimulationBanner fica em `.top-bars`). */
function HeaderFallback() {
  return (
    <header class="top-bar" data-testid="top-bar-fallback">
      <div class="brand">
        <h1 class="brand__name">{import.meta.env.VITE_PRODUCT_NAME}</h1>
      </div>
    </header>
  );
}

/** Boundary das regiões do AppShell (§12.8): a ErrorBoundary nomeada; a do cabeçalho com o fallback mínimo. */
function ShellBoundary(props: ShellBoundaryProps) {
  return <ErrorBoundary {...props} fallback={props.name === 'header' ? HeaderFallback : undefined} />;
}

/** Duração do tween dos números ao vivo (§3.6.2), num nó sem visual: um snapshot novo não re-renderiza a casca. */
function LiveTween() {
  const view = useView();
  const { live } = useTimeline();
  useTweenDuration(view?.seq ?? null, view?.instantTMin ?? null, live);
  return null;
}

type AboutModule = typeof import('../features/about/AboutDialog');

/** AboutDialog em chunk próprio (§2.2 bloco App; FE §11.3): carregado na primeira abertura e mantido. */
function AboutPortal() {
  const open = useUi().aboutOpen !== null;
  const [Dialog, setDialog] = useState<ComponentType | null>(null);
  useEffect(() => {
    if (!open || Dialog) return;
    let alive = true;
    import('../features/about/AboutDialog').then(
      (m: AboutModule) => {
        if (alive) setDialog(() => m.AboutDialog);
      },
      (err: unknown) => {
        console.warn('[app] AboutDialog indisponível:', err);
        chunkFailed();
        if (alive) closeAbout();
      },
    );
    return () => {
      alive = false;
    };
  }, [open, Dialog]);
  if (!Dialog) return null;
  return (
    <ErrorBoundary name="about" quiet>
      <Dialog />
    </ErrorBoundary>
  );
}

/** `data-hidden` com a aba oculta (§3.6.4 regra 2) e `low-power` com `deviceMemory ≤ 2` (§15.8 item 13). */
function installDocumentFlags(): () => void {
  const html = document.documentElement;
  const memory = (navigator as { deviceMemory?: number }).deviceMemory;
  if (typeof memory === 'number' && memory <= 2) html.classList.add('low-power');
  const onVisibility = () => html.toggleAttribute('data-hidden', document.hidden);
  onVisibility();
  document.addEventListener('visibilitychange', onVisibility);
  return () => document.removeEventListener('visibilitychange', onVisibility);
}

/**
 * Ouvintes globais (§2.2 bloco 5), na ordem da captura em `window`: modalidade antes do Esc (o Esc que age para a
 * propagação e a tecla ainda precisa ligar a modalidade, §15.2.1), depois os atalhos (bolha).
 */
function installGlobalListeners(): () => void {
  installReducedMotion();
  const off = [
    installKeyboardModality(),
    installEscape({
      closePicker: () => togglePicker(false),
      closeSearch: () => toggleSearch(false),
      closeAbout,
      exitTv,
      closeProfile,
      upOne,
      currentRoute,
    }),
    installShortcuts<RouteOffice>({
      toggleSearch,
      offices: () => offices.value,
      setOffice,
      oneKeyEnabled: getOneKeyShortcuts,
      zoom: mapZoom,
      // ORD-F-026, PR 6: C alterna as telas só com a troca na página (a chave e a camada ligadas)
      toggleScreen: () =>
        document.querySelector('[data-testid="screen-toggle"]') !== null &&
        setScreen(currentRoute().screen ? null : 'comments'),
    }),
    installDocumentFlags(),
    // AC-15.6-07: aba oculta sem rAF, nem o dos efeitos do Preact (app/hiddenEffects.ts)
    installHiddenEffects(),
  ];
  configureRouterEffects({
    focus: (el) => {
      focusIfKeyboard(el);
    },
    // ORD-F-091: com a página inicial na tela (fase `home`, ou `after` com a apuração fechada), o título dela
    title: phaseTitle,
  });
  off.push(installPhaseTitle());
  return () => {
    for (const f of off.reverse()) f();
    configureRouterEffects(null);
  };
}

/**
 * Texto de leitor de tela de um evento de saliência 3 (§15.3): o modelo de §13.5 na forma `sr`, com os nomes da vista
 * ao vivo e a geografia (capital, cidades do exterior); o campo `text` do fio não é usado (§13.5 é dono).
 */
function eventSr(e: ElectionEvent): string | null {
  const view = liveView.peek();
  return view ? eventText(e, view, 'sr', statics.peek().geography) : null;
}

/** DevPanel (§14.9.9; DE-B13): chunk próprio, só com `__SIM_ENABLED__` e `painel=1`; segue a fonte recriada. */
function DevTools({ runtime }: { runtime: FeedRuntime }) {
  const [Panel, setPanel] = useState<ComponentType<DevPanelHostProps> | null>(null);
  const [source, setSource] = useState(runtime.source);
  useEffect(() => {
    let alive = true;
    loadDevPanel().then(
      (P) => {
        if (alive && P) setPanel(() => P);
      },
      (err: unknown) => console.warn('[app] painel de desenvolvimento indisponível:', err),
    );
    const off = runtime.subscribe(setSource);
    return () => {
      alive = false;
      off();
    };
  }, [runtime]);
  if (!Panel) return null;
  return (
    <ErrorBoundary name="devtools" quiet>
      <Panel
        source={source}
        recreate={(q: URLSearchParams) => void runtime.recreate(q)}
        phase={phase}
        headline={headline}
      />
    </ErrorBoundary>
  );
}

export interface AppProps {
  /** Ligação viva da fonte (main.tsx); ausente nos testes de unidade da casca. */
  runtime?: FeedRuntime;
  /** Query real da página (`?fonte=…`, `painel`; §14.9.7). */
  query?: URLSearchParams;
  /**
   * A porta (ORD-F-153; app/siteGate.ts): `public` = a página principal pública, SEM `runtime` (nenhum pedido ao feed),
   * com o relógio do aparelho corrigido pela própria origem; às 17h00 a capa no lugar da apuração. `session` = o site
   * completo com o "Sair". Padrão `open` (o site de hoje).
   */
  access?: SiteAccess;
}

export function App({ runtime, query, access = 'open' }: AppProps = {}) {
  const { mode } = useLayout();
  const phone = isPhoneMode(mode);

  // Antes do primeiro quadro: nenhuma tecla do boot fica sem ouvinte.
  useLayoutEffect(() => installGlobalListeners(), []);
  // Feed e efeitos globais na montagem (§2.2.3 passos 3 e 8): o 1º ciclo aproveita a pré-busca, se tiver ≤ 10 s.
  useLayoutEffect(() => {
    // a página pública (ORD-F-153): o tique de `now` pelo relógio do aparelho corrigido pela origem, sem feed
    if (access === 'public') return startEffects({ clock: publicClock });
    if (!runtime) return;
    const stopEffects = startEffects({ clock: runtime.clock });
    runtime.start();
    return () => {
      runtime.stop();
      stopEffects();
    };
  }, [runtime, access]);
  // 26/10: quem via a apuração quando a fase virou para `after` continua nela (sem sessão, a porta decide)
  useLayoutEffect(() => (access === 'public' ? undefined : installAfterKeepsApp()), [access]);
  // useEffect do Preact roda depois do quadro desenhado: o AppShell já está na tela.
  useEffect(signalBootOk, []);
  // Fase 10 (§9.2): a malha municipal da UF em foco, depois do chunk do mapa
  useLiveMunicipalLayer();
  const panel = runtime !== undefined && devPanelWanted(query ?? new URLSearchParams());
  // ORD-F-026, PR 6: a tela de comentários (atrás de commentary_timeline) no lugar do mapa
  const screenOn = useLiveScreenOn();
  useScreenReturn(screenOn);
  // a fase do site (só muda nas fronteiras: nenhum render por segundo)
  const phase = sitePhase.value;
  // a apuração (AppShell): às 17h00 de 25/10 e, na abertura pós-apuração (`after`), depois do "Ver a apuração completa"
  // (ou com um endereço de rota na entrada); sem sessão, nunca (a capa, a abertura ou a porta)
  const showApp = access !== 'public' && (phase === 'live' || (phase === 'after' && appOpen.value));

  const slots: ShellSlots = {
    topBars: (
      <>
        <SimulationBanner />
        <PartnerBarSlot waitForData />
      </>
    ),
    header: (
      <TopBar
        screenToggle={<LiveScreenToggle variant="header" />}
        account={access === 'session' ? <SignOutButton email={sessionEmail()} onSignOut={signOut} /> : null}
      />
    ),
    // celular: barra própria (flow fixa, tablet, topo da coluna lateral no land, §6.3)
    tabs: phone ? (
      <>
        <LiveScreenToggle variant="bar" />
        <OfficeTabs variant="bar" />
      </>
    ) : null,
    notice: <NoticeBar />,
    // §7.1: herói nacional + gráfico/slot na coluna esquerda (desktop, TV); no cartão principal (tablet, land, flow),
    // o herói sem foco e o cartão do §8 com foco. §11.1: na aba Governadores, o GovernorsPanel no lugar dos dois
    left: (
      <>
        <PresidentHero region="left" />
        {/* computador estreito, sem coluna direita: os comentários abaixo do cartão principal (ORD-F-022) */}
        {screenOn ? null : <NarrowCommentarySlot />}
        <TrendSlot />
        <GovernorsSlot region="left" />
      </>
    ),
    card: (
      <>
        <PresidentHero region="card" />
        {/* tablet e celular deitado: os comentários no cartão, abaixo do herói (ORD-F-022) */}
        {screenOn ? null : <CardCommentarySlot />}
        <GovernorsSlot region="card" />
        <PlaceSlot />
      </>
    ),
    // §4.4.2: cartão do foco, ou "Por região" + "Últimas atualizações"; §4.6.2: seção "Mais" do flow (só sem foco)
    right: <RightPanels />,
    more: <MorePanels />,
    // §6.5: `map` no desktop, `inline` no celular, `footer` no fim do flow; na TV o selo vai no TopBar
    stamp: mode === 'tv' ? null : <SourceStamp variant={mode === 'desktop' ? 'map' : 'inline'} />,
    footer: mode === 'flow' ? <SourceStamp variant="footer" /> : null,
    // §9.1, DE-06 (app/mapSlot.tsx): barra estática no `index`; mapa e zoom pelo chunk `map`, depois do herói
    mapTop: <MapTopSlot />,
    map: <MapSlot />,
    zoom: <ZoomSlot />,
    // §10.2: a linha do tempo (o AppShell não monta a seção na TV)
    timeline: <TimelineSlot />,
    screen: screenOn ? <LiveScreenSlot /> : null,
  };

  return (
    <RootErrorBoundary>
      <div id="app-root" data-testid="app-root">
        {/* ORD-F-054 item 2: antes das 17h00 de 25/10, a tela da fase (pré-apuração, página inicial); às 17h00 a apuração
            entra no lugar dela, sem recarregar (src/app/sitePhase.ts; só com features.site_phases) */}
        {/* ORD-F-153: sem sessão (ou no pacote só da capa), às 17h00 a capa com a live no lugar da apuração */}
        {showApp ? <AppShell slots={slots} boundary={ShellBoundary} /> : <PhaseScreen phase={phase} access={access} />}
      </div>
      <div id="dialog-root" data-testid="dialog-root">
        {/* §5.8: sempre montada e oculta (o toque no SearchButton foca o campo na mesma tarefa, iOS) */}
        <ErrorBoundary name="search" quiet>
          <SearchPalette />
        </ErrorBoundary>
        <AboutPortal />
        {/* D4 (ORD-F-078/082): o cartão da definição matemática, só com features.math_card e só na apuração */}
        {showApp ? <MathCardSlot /> : null}
      </div>
      {/* P-87: o sensor do índice dos comentários (sem tela), com ou sem o painel */}
      {showApp ? <CommentaryProbeSlot /> : null}
      <LiveRegion clock={runtime?.clock} eventText={eventSr} />
      <LiveTween />
      <PanelsIntro />
      {panel && runtime ? <DevTools runtime={runtime} /> : null}
    </RootErrorBoundary>
  );
}
